import React, { useState, useEffect, useRef } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import LogModal from '../../components/Logs/LogModal';
import TwoFactorCard from '../../components/TwoFactor/TwoFactorCard';
import { getSettings, updateSettings } from '../../services/SettingsService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';

function Settings() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const confirmPassword = useRef('');
    const [settings, setSettings] = useState({});
    const [notification, setNotification] = useState({});

    function loadSettings() {
        return getSettings()
            .then(result => setSettings(result))
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }

    useEffect(() => { loadSettings(); }, []);

    function onRemoveGrok() {
        updateSettings({ grokKeyClear: true })
            .then(() => { setNotification({ type: 'success', text: 'Grok key removed.' }); loadSettings(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onInputChange(event) {
        setSettings(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onFormSubmit(event) {
        if ((settings.password || confirmPassword.current.value)
            && settings.password !== confirmPassword.current.value)
            return setNotification({ type: 'error', text: t('settings.passwordMismatch') });

        updateSettings(settings)
            .then(result => {
                if (result)
                    setNotification({ type: 'success', text: t('settings.saveSuccess') });
                else
                    setNotification({ type: 'error', text: result });
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">{t('settings.title')}</h1>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <button id="btnLogs" className="btn btn-primary animate-up-2 me-2" data-bs-toggle="modal" data-bs-target="#modalLogs">
                                <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2h-1.528A6 6 0 004 9.528V4z" /><path fillRule="evenodd" d="M8 10a4 4 0 00-3.446 6.032l-1.261 1.26a1 1 0 101.414 1.415l1.261-1.261A4 4 0 108 10zm-2 4a2 2 0 114 0 2 2 0 01-4 0z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
                {/* .gh-grid instead of row/col-md-6: fields reflow 2 → 1 at ANY width,
                    including a docked sidebar on a 13" laptop. */}
                <div className="card card-body border-0 shadow mb-4">
                    <h2 className="h5 mb-4">{t('settings.personalSettings')}</h2>
                    <div className="gh-grid gh-grid-wide mb-3">
                        <div className="form-group">
                            <label htmlFor="email">{t('common.email')}</label>
                            <input className="form-control" id="email" type="email" placeholder="name@company.com" defaultValue={settings.email} onChange={onInputChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">{t('settings.phone')}</label>
                            <input className="form-control" id="phone" type="text" placeholder="+1 51 123456789" defaultValue={settings.phone} onChange={onInputChange} />
                        </div>
                    </div>
                    <div className="gh-grid gh-grid-wide mb-3">
                        <div className="form-group">
                            <label htmlFor="password">{t('settings.newPassword')}</label>
                            <input className="form-control" id="password" type="password" placeholder={t('settings.newPasswordPlaceholder')} onChange={onInputChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirmPassword">{t('settings.confirmPassword')}</label>
                            <input ref={confirmPassword} className="form-control" id="confirmPassword" type="password" placeholder={t('settings.confirmPasswordPlaceholder')} />
                        </div>
                    </div>
                    <div>
                        <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>{t('common.save')}</button>
                    </div>
                </div>
                <div className="card card-body border-0 shadow mb-4">
                    <h2 className="h5 my-4">{t('settings.alertSettings')}</h2>
                    <div className="gh-grid gh-grid-wide mb-3">
                        <div className="form-group">
                            <label htmlFor="telegramBot">{t('settings.telegramBot')}</label>
                            <input className="form-control" id="telegramBot" type="text" placeholder={t('settings.telegramBotPlaceholder')} defaultValue={settings.telegramBot} onChange={onInputChange} />
                        </div>
                    </div>
                    <div className="gh-grid gh-grid-wide mb-3">
                        <div className="form-group">
                            <label htmlFor="telegramToken">{t('settings.telegramToken')}</label>
                            <input className="form-control" id="telegramToken" type="password" placeholder={t('settings.telegramTokenPlaceholder')} defaultValue={settings.telegramToken} onChange={onInputChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="telegramChat">{t('settings.telegramChat')}</label>
                            <input className="form-control" id="telegramChat" type="text" placeholder={t('settings.telegramChatPlaceholder')} defaultValue={settings.telegramChat} onChange={onInputChange} />
                        </div>
                    </div>
                    <div className="gh-grid gh-grid-wide mb-3">
                        <div className="form-group">
                            <label htmlFor="grokKey">
                                Grok API Key (Sentiment)
                                {settings.grokKeySet
                                    ? <span className="badge bg-success ms-2">configured</span>
                                    : <span className="badge bg-secondary ms-2">not set</span>}
                            </label>
                            {/* flex-nowrap keeps the Remove button on the field's line; the
                                input itself shrinks (min-width:0) instead of widening the card. */}
                            <div className="input-group flex-nowrap">
                                <input className="form-control" id="grokKey" type="password" placeholder={settings.grokKeySet ? '•••••••• (enter to replace)' : 'xai-...'} value={settings.grokKey || ''} onChange={onInputChange} style={{ minWidth: 0 }} />
                                {settings.grokKeySet
                                    ? <button className="btn btn-danger flex-shrink-0" type="button" onClick={onRemoveGrok}>Remove</button>
                                    : null}
                            </div>
                            <small className="text-muted">X.AI (Grok) key for BNB social-sentiment analysis. Save to add/replace, Remove to delete.</small>
                        </div>
                    </div>
                    <div>
                        <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>{t('common.save')}</button>
                    </div>
                </div>
                <TwoFactorCard />
                <Footer />
            </main>
            <Toast text={notification.text} type={notification.type} />
            <LogModal file="system" />
        </React.Fragment >
    );
}

export default Settings;
